import {useCallback, useMemo, useRef, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import type {
  AssignmentTypeFilter,
  ClassworkCreatedByFilter,
  SortValue
} from 'client/TeacherDashboard/types';
import type {AssignmentFilterInput, AssignmentSortInput} from 'gql/types';
import {AssignmentSortField, AssignmentStatus, SortOrder} from 'gql/types';
import {normalizeError} from 'lib/normalizeError';

import type {StatusSegment} from '../constants';
import type {TeacherAssignmentRowFieldsFragment} from '../queries/TeacherAssignments.queries.generated';
import {useTeacherAssignmentsQuery} from '../queries/TeacherAssignments.queries.generated';

const PAGE_SIZE = 25;

export type AssignmentSortValue = 'recentlyEdited' | 'name' | 'dueDate';

const STATUS_ENUM: Record<StatusSegment, AssignmentStatus> = {
  open: AssignmentStatus.Open,
  closed: AssignmentStatus.Closed,
  scheduled: AssignmentStatus.Scheduled,
  draft: AssignmentStatus.Draft
};

export interface TeacherAssignmentsFilters {
  search: string;
  segment: StatusSegment;
  createdBy: ClassworkCreatedByFilter;
  /** The acting (possibly masqueraded) teacher id, for the created-by filter. */
  actorTeacherId: string;
  sort: SortValue<AssignmentSortValue>;
  /** Scopes the per-educator status/date calcs to one class; omit for cross-class. */
  classroomId?: string;
  /** When masquerading, the acted-as teacher's id — scopes the index to them; omit for your own. */
  asEducatorId?: string;
  /** 'all' = no type filter; 'assignment'/'exam' filter on the raw assignment type. */
  assignmentType: AssignmentTypeFilter;
  /** Inclusive due-date range bounds (ISO); null = that bound is unset. */
  dueDateStart?: string | null;
  dueDateEnd?: string | null;
}

// Shared with useAssignmentStatusCounts so the selector counts scope exactly like the list.
export const buildAssignmentFilter = ({
  search,
  segment,
  createdBy,
  actorTeacherId,
  classroomId,
  assignmentType,
  dueDateStart,
  dueDateEnd
}: Omit<TeacherAssignmentsFilters, 'sort'>): AssignmentFilterInput => {
  const classroomIds = classroomId ? [classroomId] : undefined;
  const filter: AssignmentFilterInput = {
    educatorStatus: {eq: STATUS_ENUM[segment], input: classroomIds && {classroomIds}}
  };
  if (search) {
    filter.name = {ilike: `%${search}%`};
  }
  // Drafts have no creator/type/due dates to filter on — selections persist in state but only
  // apply to the published tabs, so returning from Drafts restores them.
  if (segment !== 'draft') {
    if (createdBy === 'you') {
      filter.teacherId = {eq: actorTeacherId};
    } else if (createdBy === 'someoneElse') {
      filter.teacherId = {notEq: actorTeacherId};
    }
    if (assignmentType !== 'all') {
      filter.assignmentType = {eq: assignmentType};
    }
    // Overlap semantics: match when the row's due-date span [min, max] intersects the selected
    // range, i.e. any in-scope class/student due date can fall inside it. Scoped to the class in
    // the class-specific view so the filter judges the same dates the rows display.
    if (dueDateStart) {
      filter.maxDueDate = {greaterThanOrEqual: dueDateStart, input: classroomIds && {classroomIds}};
    }
    if (dueDateEnd) {
      filter.minDueDate = {lessThanOrEqual: dueDateEnd, input: classroomIds && {classroomIds}};
    }
  }
  if (classroomIds) {
    filter.assignedToClassrooms = {eq: true, input: {classroomIds}};
  }
  return filter;
};

const buildAssignmentSort = (
  {field, order}: SortValue<AssignmentSortValue>,
  classroomId?: string
): AssignmentSortInput[] => {
  const sortOrder = order === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  if (field === 'name') {
    return [{field: AssignmentSortField.Name, order: sortOrder}];
  }
  if (field === 'dueDate') {
    // Earliest due date, scoped to the class in the class-specific view (mirrors the row's
    // minDueDate(classroomIds:) display); Name asc as a stable tiebreak.
    return [
      {
        field: AssignmentSortField.MinDueDate,
        order: sortOrder,
        minDueDateInput: classroomId ? {classroomIds: [classroomId]} : undefined
      },
      {field: AssignmentSortField.Name, order: SortOrder.Asc}
    ];
  }
  // Recently edited; Name asc as a stable tiebreak.
  return [
    {field: AssignmentSortField.UpdatedAt, order: sortOrder},
    {field: AssignmentSortField.Name, order: SortOrder.Asc}
  ];
};

interface UseTeacherAssignmentsResult {
  assignments: TeacherAssignmentRowFieldsFragment[];
  isLoading: boolean;
  /** The list query failed — show the retry panel (refetch is the retry handler). */
  hasError: boolean;
  hasNextPage: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  /** Reset pagination and re-pull page one (e.g. after a delete). */
  refetch: () => void;
}

export const useTeacherAssignments = (
  filters: TeacherAssignmentsFilters
): UseTeacherAssignmentsResult => {
  const {
    search,
    segment,
    createdBy,
    actorTeacherId,
    classroomId,
    asEducatorId,
    assignmentType,
    dueDateStart,
    dueDateEnd
  } = filters;
  const {field: sortField, order: sortOrder} = filters.sort;

  // Keyed on primitives, not the fresh-per-render filters object, to keep loadMore/query stable.
  const variables = useMemo(
    () => ({
      classroomIds: classroomId ? [classroomId] : undefined,
      filter: buildAssignmentFilter({
        search,
        segment,
        createdBy,
        actorTeacherId,
        classroomId,
        assignmentType,
        dueDateStart,
        dueDateEnd
      }),
      sort: buildAssignmentSort({field: sortField, order: sortOrder}, classroomId),
      first: PAGE_SIZE,
      asEducatorId
    }),
    [
      search,
      segment,
      createdBy,
      actorTeacherId,
      classroomId,
      asEducatorId,
      assignmentType,
      dueDateStart,
      dueDateEnd,
      sortField,
      sortOrder
    ]
  );

  // Keyset pages beyond the first are appended in local state (the field is
  // arg-keyed, so Apollo can't merge pages itself without a typePolicy).
  const [moreResults, setMoreResults] = useState<TeacherAssignmentRowFieldsFragment[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // endKeyset of the last page = the `after` cursor for the next fetch.
  const nextCursorRef = useRef<string | undefined>(undefined);
  // Drops stale fetchMore responses.
  const requestGenRef = useRef(0);

  const {
    data,
    loading,
    error,
    fetchMore,
    refetch: apolloRefetch
  } = useTeacherAssignmentsQuery({fetchPolicy: 'cache-and-network', variables});

  const resetPages = useCallback(() => {
    setMoreResults([]);
    nextCursorRef.current = undefined;
    requestGenRef.current += 1;
  }, []);

  // Reset pagination when the query variables change. Track the previous value in state (the
  // React "adjust state during render" pattern) rather than a ref written during render.
  const [prevVariables, setPrevVariables] = useState(variables);
  if (prevVariables !== variables) {
    setPrevVariables(variables);
    // oxlint-disable-next-line react/react-compiler -- cursor/generation refs must be invalidated synchronously with the render-phase reset so this render doesn't paginate with a stale cursor
    resetPages();
  }

  const page = data?.assignmentsAsEducator;
  const assignments = useMemo(() => {
    const seen = new Set<string>();
    const merged: TeacherAssignmentRowFieldsFragment[] = [];
    for (const assignment of [...(page?.results ?? []), ...moreResults]) {
      if (!seen.has(assignment.id)) {
        seen.add(assignment.id);
        merged.push(assignment);
      }
    }
    return merged;
  }, [page?.results, moreResults]);

  // oxlint-disable-next-line react/react-compiler -- pagination cursor ref read during render; only advanced by loadMore callbacks, and reset paths rerender via state
  const nextCursor = nextCursorRef.current ?? page?.endKeyset ?? null;
  const fetchedCount = (page?.results?.length ?? 0) + moreResults.length;
  const hasNextPage = Boolean(nextCursor) && fetchedCount < (page?.count ?? 0);

  const loadMore = useCallback(() => {
    if (loading || isLoadingMore || !hasNextPage || !nextCursor) {
      return;
    }
    const generation = requestGenRef.current;
    setIsLoadingMore(true);
    fetchMore({variables: {...variables, after: nextCursor}})
      .then((result) => {
        if (generation !== requestGenRef.current) {
          return;
        }
        const nextPage = result.data?.assignmentsAsEducator;
        setMoreResults((current) => [...current, ...(nextPage?.results ?? [])]);
        nextCursorRef.current = nextPage?.endKeyset ?? undefined;
      })
      .catch((fetchMoreError) => {
        if (generation !== requestGenRef.current) {
          return;
        }
        addToast({
          color: 'negative',
          title: "Couldn't load more assignments",
          message: normalizeError(fetchMoreError).message
        });
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [fetchMore, hasNextPage, isLoadingMore, loading, nextCursor, variables]);

  const refetch = useCallback(() => {
    resetPages();
    // Swallow the rejection: apolloRefetch rejects under errorPolicy 'none', but the failure is
    // already surfaced reactively via `error`/hasError — an uncaught reject would just be HB noise.
    void apolloRefetch().catch(() => {});
  }, [apolloRefetch, resetPages]);

  return {
    assignments,
    isLoading: loading && !data,
    hasError: error !== undefined,
    hasNextPage,
    isLoadingMore,
    loadMore,
    refetch
  };
};
